import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { API } from '@/App';
import axios from 'axios';
import { ArrowLeft, Clock, MapPin, Calendar, User, Linkedin, FileText, CheckCircle2, XCircle, Eye, ExternalLink } from 'lucide-react';

const CompanyProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedApp, setSelectedApp] = useState(null);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [projectsRes, appsRes] = await Promise.all([
        axios.get(`${API}/company/projects`),
        axios.get(`${API}/applications?project_id=${id}`)
      ]);
      
      const foundProject = projectsRes.data.find(p => p.id === id);
      if (!foundProject) {
        toast.error('Project not found');
        navigate('/company/projects');
        return;
      }
      
      setProject(foundProject);
      setApplications(appsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const updateApplicationStatus = async (appId, newStatus) => {
    setUpdating(true);
    try {
      await axios.put(`${API}/applications/${appId}`, { status: newStatus });
      toast.success('Application status updated');
      fetchData();
      setSelectedApp(null);
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const filteredApps = filter === 'all' 
    ? applications 
    : applications.filter(app => app.status === filter);

  const statusColors = {
    submitted: 'badge-neutral',
    under_review: 'badge-warning',
    shortlisted: 'badge-primary',
    intro_scheduled: 'badge-success',
    not_selected: 'badge-error',
  };

  const functionColors = {
    product: 'badge-primary',
    design: 'badge-accent',
    engineering: 'bg-emerald-100 text-emerald-700',
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="container-custom py-12">
          <div className="animate-pulse">
            <div className="h-8 bg-slate-200 rounded w-1/3 mb-4"></div>
            <div className="h-4 bg-slate-200 rounded w-1/4 mb-8"></div>
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 h-96 bg-slate-200 rounded"></div>
              <div className="h-64 bg-slate-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!project) return null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="company-project-detail">
      <Header />

      <div className="container-custom py-12">
        <button
          onClick={() => navigate('/company/projects')}
          className="flex items-center text-sm text-slate-500 hover:text-slate-700 mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Projects
        </button>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Project Header */}
            <Card className="p-6" data-testid="project-header">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h1 className="heading-3 mb-2">{project.title}</h1>
                  <div className="flex items-center gap-2">
                    <span className={`${functionColors[project.function]} px-2.5 py-0.5 rounded-full text-xs font-semibold`}>
                      {project.function}
                    </span>
                    <span className={`${statusColors[project.status]} text-xs`}>
                      {project.status}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                <span className="flex items-center">
                  <Clock className="h-4 w-4 mr-1 text-slate-400" />
                  {project.hours_per_week}h/week
                </span>
                <span className="flex items-center">
                  <Calendar className="h-4 w-4 mr-1 text-slate-400" />
                  {project.duration}
                </span>
                <span className="flex items-center">
                  <MapPin className="h-4 w-4 mr-1 text-slate-400" />
                  {project.location_type}
                </span>
              </div>
            </Card>

            {/* Applicants */}
            <Card className="p-6" data-testid="applicants-list">
              <div className="flex items-center justify-between mb-6">
                <h2 className="heading-4">
                  Applicants ({applications.length})
                </h2>
                <Select value={filter} onValueChange={setFilter}>
                  <SelectTrigger className="w-40" data-testid="applicant-filter">
                    <SelectValue placeholder="Filter" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="submitted">Submitted</SelectItem>
                    <SelectItem value="under_review">Under Review</SelectItem>
                    <SelectItem value="shortlisted">Shortlisted</SelectItem>
                    <SelectItem value="intro_scheduled">Intro Scheduled</SelectItem>
                    <SelectItem value="not_selected">Not Selected</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {filteredApps.length > 0 ? (
                <div className="space-y-4">
                  {filteredApps.map((app) => (
                    <div 
                      key={app.id} 
                      className="flex items-start justify-between p-4 bg-slate-50 rounded-lg"
                      data-testid={`applicant-${app.id}`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <User className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <h4 className="font-medium text-slate-900">{app.talent_name}</h4>
                            <p className="text-sm text-slate-500">{app.talent_headline || app.talent_function}</p>
                          </div>
                        </div>
                        
                        <p className="text-sm text-slate-600 line-clamp-2 mb-3 ml-13">
                          "{app.pitch_note}"
                        </p>

                        <div className="flex items-center gap-4 ml-13">
                          {app.talent_linkedin && (
                            <a 
                              href={app.talent_linkedin} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-sm text-primary hover:underline flex items-center"
                            >
                              <Linkedin className="h-4 w-4 mr-1" />
                              LinkedIn
                            </a>
                          )}
                          {app.talent_resume && (
                            <a 
                              href={app.talent_resume} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-sm text-primary hover:underline flex items-center"
                            >
                              <FileText className="h-4 w-4 mr-1" />
                              Resume
                            </a>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col items-end gap-2 ml-4">
                        <span className={`${statusColors[app.status]} text-xs`}>
                          {app.status.replace('_', ' ')}
                        </span>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setSelectedApp(app)}
                          data-testid={`review-${app.id}`}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Review
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <User className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <h3 className="font-medium text-slate-900 mb-1">No applicants yet</h3>
                  <p className="text-slate-500 text-sm">
                    {filter !== 'all' 
                      ? `No applicants with "${filter.replace('_', ' ')}" status`
                      : 'Applications will appear here once talent applies'}
                  </p>
                </div>
              )}
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card className="p-6" data-testid="project-stats">
              <h3 className="font-semibold text-slate-900 mb-4">Quick Stats</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-500">Total Applicants</span>
                  <span className="font-medium">{applications.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Shortlisted</span>
                  <span className="font-medium">{applications.filter(a => a.status === 'shortlisted').length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Intros Scheduled</span>
                  <span className="font-medium">{applications.filter(a => a.status === 'intro_scheduled').length}</span>
                </div>
              </div>
            </Card>

            <Card className="p-6" data-testid="project-info">
              <h3 className="font-semibold text-slate-900 mb-4">Project Info</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Duration</span>
                  <span className="font-medium">{project.duration}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Hours/Week</span>
                  <span className="font-medium">{project.hours_per_week}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Location</span>
                  <span className="font-medium capitalize">{project.location_type}</span>
                </div>
                {project.budget_range && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Budget</span>
                    <span className="font-medium">{project.budget_range}</span>
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Applicant Review Modal */}
      <Dialog open={!!selectedApp} onOpenChange={() => setSelectedApp(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="applicant-review-modal">
          <DialogHeader>
            <DialogTitle>Review Applicant</DialogTitle>
          </DialogHeader>
          
          {selectedApp && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-7 w-7 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">{selectedApp.talent_name}</h3>
                  <p className="text-sm text-slate-500">{selectedApp.talent_headline || selectedApp.talent_function}</p>
                </div>
              </div>

              <div className="flex gap-4">
                {selectedApp.talent_linkedin && (
                  <a 
                    href={selectedApp.talent_linkedin} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex-1"
                  >
                    <Button variant="outline" className="w-full">
                      <Linkedin className="h-4 w-4 mr-2" />
                      LinkedIn
                    </Button>
                  </a>
                )}
                {selectedApp.talent_resume && (
                  <a 
                    href={selectedApp.talent_resume} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex-1"
                  >
                    <Button variant="outline" className="w-full">
                      <FileText className="h-4 w-4 mr-2" />
                      Resume
                    </Button>
                  </a>
                )}
              </div>

              <div>
                <p className="text-sm text-slate-500 mb-2">Pitch Note</p>
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{selectedApp.pitch_note}</p>
                </div>
              </div>

              <div>
                <p className="text-sm text-slate-500 mb-2">Current Status</p>
                <span className={`${statusColors[selectedApp.status]}`}>
                  {selectedApp.status.replace('_', ' ')}
                </span>
              </div>

              <div>
                <p className="text-sm text-slate-500 mb-3">Update Status</p>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    onClick={() => updateApplicationStatus(selectedApp.id, 'under_review')}
                    disabled={updating}
                    className="text-sm"
                  >
                    Mark as Reviewing
                  </Button>
                  <Button
                    onClick={() => updateApplicationStatus(selectedApp.id, 'shortlisted')}
                    disabled={updating}
                    className="btn-primary text-sm"
                  >
                    <CheckCircle2 className="h-4 w-4 mr-1" />
                    Shortlist
                  </Button>
                  <Button
                    onClick={() => updateApplicationStatus(selectedApp.id, 'intro_scheduled')}
                    disabled={updating}
                    className="btn-accent text-sm"
                  >
                    <Calendar className="h-4 w-4 mr-1" />
                    Schedule Intro
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => updateApplicationStatus(selectedApp.id, 'not_selected')}
                    disabled={updating}
                    className="text-sm text-red-600 hover:bg-red-50"
                  >
                    <XCircle className="h-4 w-4 mr-1" />
                    Pass
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default CompanyProjectDetail;
